import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AuthContext - Sprint 2 / T-102b
 * Provides { organizer, loading, login, loginWithWidgetToken, logout, updateProfile } to the tree.
 * On mount, calls GET /api/auth/me to rehydrate from the session cookie.
 */
import { createContext, useCallback, useContext, useEffect, useState, } from "react";
import { api, AppError } from "@/lib/api";
// ── Context ──────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);
// ── Provider ─────────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
    const [state, setState] = useState({
        organizer: null,
        loading: true,
    });
    // On mount: fetch /api/auth/me to rehydrate from cookie.
    useEffect(() => {
        api
            .get("/api/auth/me")
            .then(({ data }) => {
            setState({ organizer: data, loading: false });
        })
            .catch((err) => {
            // 401 is expected when no session - silently clear.
            // Any other error: also clear, but don't break the app.
            const status = err instanceof AppError ? err.status : 0;
            if (status !== 401) {
                console.warn("[AuthContext] /me error", err);
            }
            setState({ organizer: null, loading: false });
        });
    }, []);
    const login = useCallback((orgData) => {
        setState({ organizer: orgData, loading: false });
    }, []);
    const loginWithWidgetToken = useCallback(async (accessToken) => {
        const res = await api.post("/api/auth/verify-widget-token", { accessToken });
        setState({ organizer: res.data, loading: false });
    }, []);
    const logout = useCallback(async () => {
        try {
            await api.post("/api/auth/logout", {});
        }
        catch {
            // Best-effort: clear client state regardless.
        }
        setState({ organizer: null, loading: false });
    }, []);
    const updateProfile = useCallback(async (updates) => {
        const res = await api.patch("/api/auth/me", updates);
        setState((prev) => ({ ...prev, organizer: res.data }));
    }, []);
    return (_jsx(AuthContext.Provider, { value: { ...state, login, loginWithWidgetToken, logout, updateProfile }, children: children }));
}
// ── Hook ─────────────────────────────────────────────────────────────────────
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used inside <AuthProvider>");
    }
    return ctx;
}
// ── RequireOrganizer HOC ──────────────────────────────────────────────────────
// Redirect to /login when no session. Renders null while loading.
import { Navigate } from "react-router-dom";
export function RequireOrganizer({ children }) {
    const { organizer, loading } = useAuth();
    if (loading) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center", children: _jsx("div", { className: "w-6 h-6 rounded-full border-2 border-border-subtle border-t-[#9868cc] animate-spin", "aria-label": "Loading" }) }));
    }
    if (!organizer) {
        return _jsx(Navigate, { to: "/login", replace: true });
    }
    return _jsx(_Fragment, { children: children });
}
